import { Modal, ModalFooterAction } from "components/primitives/Modal";
import { ModalsContext } from "contexts/ModalsContext";
import React, { useContext, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";

import { INITIAL_DATA, MODAL_NEW_NUMBER_SPAN, StepSettings } from "./constants";
import { NumberSettingData } from "./interfaces";
import { roundToFraction } from "./numberToFraction";
import { divideNumber, validationNumbersFunc } from "./utils";

interface ModalNumberSpanProps {
  onSetSpanSettings: (data: NumberSettingData, steps: number[]) => void;
  config: { [k: string]: any };
  container: React.RefObject<any>;
}

export const ModalNumberSpan = (props: ModalNumberSpanProps) => {
  const { onSetSpanSettings, config, container } = props;
  const modalsContext = useContext(ModalsContext);

  const [numberSetting, setNumberSetting] = useState<NumberSettingData>(INITIAL_DATA);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setNumberSetting((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSave = () => {
    const message = validationNumbersFunc(config, numberSetting);

    if (message) {
      setErrorMessage(message);

      return;
    }

    const roundedOperandNumber = roundToFraction(
      numberSetting.operandNumber,
      StepSettings[config.stepSize],
    );
    const updatedData = {
      operand: numberSetting.operand,
      operandNumber: roundedOperandNumber,
      startNumber: roundToFraction(numberSetting.startNumber, StepSettings[config.stepSize]),
    };
    const steps = divideNumber(
      roundedOperandNumber,
      numberSetting.operand,
      StepSettings[config.stepSize],
    );

    setErrorMessage("");
    onSetSpanSettings(updatedData, steps);
    setNumberSetting(INITIAL_DATA);
    modalsContext.closeModal();
  };

  const handleClose = () => {
    modalsContext.closeModal();
    setNumberSetting(INITIAL_DATA);
    setErrorMessage("");
  };

  const onFieldDefocus = () => {
    setNumberSetting((prevState) => ({
      ...prevState,
      startNumber: Number(numberSetting.startNumber)
        ? roundToFraction(numberSetting.startNumber, StepSettings[config.stepSize])
        : numberSetting.startNumber,
      operandNumber: Number(numberSetting.operandNumber)
        ? roundToFraction(numberSetting.operandNumber, StepSettings[config.stepSize])
        : numberSetting.operandNumber,
    }));
  };

  const footerActions: ModalFooterAction[] = [
    {
      key: "cancel",
      textBtn: "Cancel",
      color: "var(--danger)",
      action: handleClose,
    },
    {
      key: "save",
      textBtn: "Create",
      color: "var(--primary)",
      action: handleSave,
      disabled: !numberSetting.startNumber && !numberSetting.operandNumber,
    },
  ];

  return (
    <>
      <Modal
        isOpen={modalsContext.modalData.name === MODAL_NEW_NUMBER_SPAN}
        footerActions={footerActions}
        className="modal-md"
        backdropClassName="modal-number-line-backdrop"
        container={container}
      >
        <Title>Create a new number span</Title>

        <FieldBox>
          <Form.Control
            type="text"
            name="startNumber"
            placeholder="e.g. 10"
            value={numberSetting.startNumber}
            onChange={handleChange}
            onBlur={onFieldDefocus}
          />

          <Form.Control
            as="select"
            name="operand"
            value={numberSetting.operand}
            onChange={handleChange}
          >
            <option value="plus">+</option>
            <option value="minus">-</option>
          </Form.Control>

          <Form.Control
            type="text"
            name="operandNumber"
            placeholder="e.g. 5"
            value={numberSetting.operandNumber}
            onChange={handleChange}
            onBlur={onFieldDefocus}
          />
        </FieldBox>

        <ErrorMessage>{errorMessage}</ErrorMessage>
      </Modal>
    </>
  );
};

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 12pt;
  text-align: center;
`;

const ErrorMessage = styled("div")`
  color: var(--danger);
  text-align: center;
`;

const FieldBox = styled(Form.Group)`
  display: flex;
  gap: 20px;
  margin-top: 30px;
  margin-bottom: 0;

  select {
    max-width: 60px;
  }
`;
